import React, { useRef, useState, useEffect } from "react";
import { Stage, Layer, Line } from "react-konva";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import ResultModal from "./ResultModal";
import { drawingPrompts } from "../data/drawingPrompts";

const DrawingCanvas = () => {
  const [lines, setLines] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [showModal, setShowModal] = useState(false);
  const [drawingURL, setDrawingURL] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [points, setPoints] = useState([]);
  const stageRef = useRef(null);

  useEffect(() => {
    let timer;
    if (isDrawing && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleTimeUp();
    }
    return () => clearInterval(timer);
  }, [timeLeft, isDrawing]);

  const getRandomPrompt = () => {
    const randomIndex = Math.floor(Math.random() * drawingPrompts.length);
    return drawingPrompts[randomIndex];
  };

  const handleMouseDown = (e) => {
    if (!isDrawing) return;
    setIsMouseDown(true);
    const pos = e.target.getStage().getPointerPosition();
    setPoints([pos]);
    setLines([...lines, { points: [pos.x, pos.y] }]);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !isMouseDown) return;
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();

    setPoints((points) => [...points, point]);

    const lastPoints = [...points, point].slice(-3);
    if (lastPoints.length >= 3) {
      const avgX =
        lastPoints.reduce((sum, p) => sum + p.x, 0) / lastPoints.length;
      const avgY =
        lastPoints.reduce((sum, p) => sum + p.y, 0) / lastPoints.length;

      let lastLine = lines[lines.length - 1];
      lastLine.points = lastLine.points.concat([avgX, avgY]);
      lines.splice(lines.length - 1, 1, lastLine);
      setLines([...lines]);
    }
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  const startDrawing = () => {
    setIsDrawing(true);
    setTimeLeft(30);
    setCurrentPrompt(getRandomPrompt());
    setLines([]);
  };

  const handleReset = () => {
    if (
      window.confirm(
        "Are you sure you want to reset? Your drawing will not be saved."
      )
    ) {
      setIsDrawing(false);
      setLines([]);
      setTimeLeft(30);
    }
  };

  const handleTimeUp = async () => {
    setIsDrawing(false);

    if (stageRef.current) {
      const drawingDataURL = stageRef.current.toDataURL();
      setDrawingURL(drawingDataURL);
      setShowModal(true);
    }
  };

  const handleSubmit = async (formData) => {
    if (stageRef.current) {
      const drawingDataURL = stageRef.current.toDataURL();

      try {
        await addDoc(collection(db, "drawings"), {
          imageUrl: drawingDataURL,
          timestamp: serverTimestamp(),
          name: formData.name,
          email: formData.email,
          prompt: currentPrompt,
          emailSent: false,
        });

        setShowModal(false);
        window.alert("Thanks for playing! Check your email for your drawing!");
        window.location.reload();
      } catch (error) {
        console.error("Error submitting drawing:", error);
        window.alert(error.message);
      }
    }
  };

  const formatTime = (seconds) => {
    const secs = seconds % 60;
    return `${secs.toString().padStart(2, "0")} seconds`;
  };

  // Add this function to prevent scrolling
  const preventScroll = (e) => {
    e.preventDefault();
  };

  // Add useEffect to add/remove touch event listeners
  useEffect(() => {
    const stage = stageRef.current;
    if (stage) {
      stage.content.addEventListener("touchmove", preventScroll, {
        passive: false,
      });
      stage.content.addEventListener("touchstart", preventScroll, {
        passive: false,
      });
    }

    return () => {
      if (stage) {
        stage.content.removeEventListener("touchmove", preventScroll);
        stage.content.removeEventListener("touchstart", preventScroll);
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center w-full z-50">
      <div className="border-2 border-indigo-200 rounded-xl overflow-hidden shadow-lg">
        <div className="flex gap-6 items-center justify-between bg-indigo-100 w-full p-3">
          <div
            className={`text-xl font-bold w-32 ${
              timeLeft <= 10 ? "text-red-500 animate-pulse" : "text-indigo-500"
            }`}
          >
            {formatTime(timeLeft)}
          </div>
          {!isDrawing ? (
            <button
              onClick={startDrawing}
              className="px-6 py-2 bg-gradient-to-br from-pink-500 to-yellow-400 text-white text-xl font-semibold rounded-full
                     hover:bg-indigo-700 transition-colors duration-200"
            >
              Start!
            </button>
          ) : (
            <>
              <div className="text-xl text-indigo-900">
                <strong>Draw:</strong> {currentPrompt}
              </div>
              <button
                onClick={handleReset}
                className="px-6 py-2 bg-gradient-to-br from-pink-500 to-yellow-400 text-white text-lg font-semibold rounded-full
                       hover:bg-indigo-700 transition-colors duration-200"
              >
                Reset
              </button>
            </>
          )}
        </div>
        <Stage
          width={800}
          height={480}
          onMouseDown={handleMouseDown}
          onMousemove={handleMouseMove}
          onMouseup={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
          ref={stageRef}
          style={{
            background: "white",
            touchAction: "none", // Add this to prevent touch scrolling
          }}
        >
          <Layer>
            {lines.map((line, i) => (
              <Line
                key={i}
                points={line.points}
                stroke="#808080"
                strokeWidth={5}
                tension={0.7}
                lineCap="round"
                lineJoin="round"
                globalCompositeOperation="source-over"
              />
            ))}
          </Layer>
        </Stage>
      </div>
      <ResultModal
        isOpen={showModal}
        drawingURL={drawingURL}
        prompt={currentPrompt}
        onSubmit={handleSubmit}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default DrawingCanvas;
